import { assertNever } from "encamp-shared/src/utils/assertNever";
import {
  CersDecisionStatus,
  CersRejectionResolutionType,
  ResolutionStatus,
  StaffCersSubmittalsQuery,
} from "generated-graphql/graphql";

import { cersTabPath } from "../CersTabs";

/**
 * Builds the same-grid "facility timeline" URL: the CERS submittals route scoped
 * to one facility via the `facility` omnisearch keyed term the V2 grid reads from
 * the `omnisearch` URL param. Default sort is submittedDate DESC, giving a
 * reverse-chronological "timeline" for free. Inner double quotes are stripped so
 * they can't terminate the quoted term early.
 */
export function facilityTimelineUrl(facilityName: string): string {
  const omnisearch = `facility:"${facilityName.replace(/"/g, "")}"`;
  return `/staff/cers/submittals?omnisearch=${encodeURIComponent(omnisearch)}`;
}

/** UTC date like "Jun 24, 2025"; "—" when missing. Shared by the CERS drawers. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** A single CERS submittal row as returned by the staff submittals query. */
export type CersSubmittalRow = StaffCersSubmittalsQuery["staffCersSubmittals"]["items"][number];

/**
 * Orders module decisions with not-accepted (rejected) modules first — the
 * grid truncates to the first few, and rejections are what staff scan for, so
 * they must never be the ones clipped. Stable otherwise (preserves the query's
 * order within each group).
 */
export function sortModulesNotAcceptedFirst<T extends { status: CersDecisionStatus }>(
  decisions: readonly T[]
): T[] {
  const rank = (status: CersDecisionStatus) => (status === CersDecisionStatus.NotAccepted ? 0 : 1);
  return [...decisions].sort((a, b) => rank(a.status) - rank(b.status));
}

/**
 * Color map for CersDecisionStatus chips:
 * Accepted=success, NotAccepted=error, UnderReview/Submitted=info,
 * Certified/NotApplicable=default
 */
export function decisionStatusColor(
  status: CersDecisionStatus
): "success" | "error" | "info" | "default" {
  switch (status) {
    case CersDecisionStatus.Accepted:
      return "success";
    case CersDecisionStatus.NotAccepted:
      return "error";
    case CersDecisionStatus.UnderReview:
    case CersDecisionStatus.Submitted:
      return "info";
    default:
      return "default";
  }
}

/**
 * Terminal-state copy for a rejection finding, shown as the right endpoint of
 * the drawer's "Not accepted → …" progression. A resolved finding with no
 * resolution type was auto-resolved at ingest (a newer accepted submittal
 * superseded the rejection).
 */
export function resolutionTerminalLabel(resolution: {
  status: ResolutionStatus;
  resolutionType?: CersRejectionResolutionType | null;
  revisionReportId?: string | null;
}): string {
  const { status, resolutionType, revisionReportId } = resolution;
  switch (status) {
    case ResolutionStatus.Unresolved:
      return "Open";
    case ResolutionStatus.Pending:
      // A report is only linked once the revision is actually being worked;
      // without one the revision is merely queued, and the link points back to
      // the rejection rather than a report — keep the chip honest about that.
      return revisionReportId ? "Revision in progress" : "Revision pending";
    case ResolutionStatus.Ignored:
      return "Dismissed";
    case ResolutionStatus.Resolved:
      if (resolutionType === CersRejectionResolutionType.RevisionFiled) {
        return "Resolved via revision";
      }
      if (resolutionType === CersRejectionResolutionType.Acknowledged) {
        return "Acknowledged";
      }
      return "Auto-resolved";
    default:
      return assertNever(status);
  }
}

/**
 * The action link for an unfinished finding, or null when there's nothing left
 * to work (resolved / acknowledged / auto-resolved / dismissed). A revision
 * already in progress links straight to that report; anything else routes into
 * the rejections tracker where it can be worked.
 */
export function resolutionLink(resolution: {
  status: ResolutionStatus;
  findingId: string;
  revisionReportId?: string | null;
}): { to: string; label: string } | null {
  const { status, findingId, revisionReportId } = resolution;
  if (status === ResolutionStatus.Pending && revisionReportId) {
    return { to: `/staff/fulfillment/r/${revisionReportId}`, label: "View revision" };
  }
  if (status === ResolutionStatus.Unresolved || status === ResolutionStatus.Pending) {
    return { to: `${cersTabPath("rejections")}?findingId=${findingId}`, label: "View rejection" };
  }
  return null;
}
